"use client"

import { ROOM_TYPE_MAPPINGS } from "@/src/utils/api-value-mappings"

interface WizardStepperProps {
  steps: string[]
  currentStep: number
  selections?:
    | {
        roomType?: string
        roomSize?: string
        style?: string
        colorDetails?: string
      }
    | string[] // Added support for array format
}

export function WizardStepper({ steps, currentStep, selections }: WizardStepperProps) {
  const getSelectionDisplay = (stepIndex: number): string | null => {
    if (!selections) return null

    if (Array.isArray(selections)) {
      return selections[stepIndex] || null
    }

    switch (stepIndex) {
      case 0: // Room Type
        if (selections.roomType) {
          const roomTypeObj = ROOM_TYPE_MAPPINGS.find((room) => room.value === selections.roomType)
          return roomTypeObj ? roomTypeObj.name : selections.roomType
        }
        return null
      case 1: // Room Size
        return selections.roomSize ? selections.roomSize.charAt(0).toUpperCase() + selections.roomSize.slice(1) : null
      case 2: // Style
        if (selections.style) {
          return selections.style
            .replace(/-theme$/, "") // Remove "-theme" suffix
            .split("-")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ")
        }
        return null
      case 3: // Color Preferences
        return selections.colorDetails ? "Colors Selected" : null
      case 4: // Generated Designs
        return "Generated Designs"
      default:
        return null
    }
  }

  const getStepLabel = (stepIndex: number, originalLabel: string): string => {
    const stepNumber = stepIndex + 1
    const isCompleted = stepNumber < currentStep || (stepNumber === currentStep && stepNumber === steps.length)

    if (isCompleted) {
      const selectionText = getSelectionDisplay(stepIndex)
      if (selectionText) {
        return selectionText
      }
    }

    return originalLabel
  }

  const getStepColor = (stepIndex: number, isActive: boolean, isCompleted: boolean): string => {
    if (isActive) return "bg-[#40A1AB] text-white"
    if (isCompleted) return "bg-[#40A1AB] text-white"
    return "bg-gray-200 text-gray-500"
  }

  const getTextColor = (stepIndex: number, isActive: boolean, isCompleted: boolean): string => {
    if (isCompleted && getSelectionDisplay(stepIndex)) return "text-slate-600 font-bold"
    if (isActive) return "text-[#40A1AB] font-medium"
    if (!isCompleted) return "text-gray-500"

    return "text-slate-600 font-bold"
  }

  return (
    <div className="w-full">
      {/* Desktop view */}
      <div className="hidden md:block">
        <div className="relative flex items-center justify-between mb-8">
          {/* Connecting lines - gray background line only */}
          <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200" />

          {steps.map((step, index) => {
            const stepNumber = index + 1
            const isActive = stepNumber === currentStep
            const isCompleted = stepNumber < currentStep || (stepNumber === currentStep && stepNumber === steps.length)
            const displayLabel = getStepLabel(index, step)

            return (
              <div key={index} className="flex flex-col items-center z-10">
                {/* Step circle */}
                <div className="relative">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${getStepColor(index, isActive, isCompleted)}`}
                  >
                    {stepNumber}
                  </div>
                </div>

                {/* Step label - now shows selection for completed steps */}
                <div className="mt-2 w-24 h-8 flex items-start justify-center">
                  <span className={`text-xs text-center leading-tight ${getTextColor(index, isActive, isCompleted)}`}>
                    {displayLabel}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Mobile view */}
      <div className="md:hidden">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-[#40A1AB]">
            Step {currentStep} of {steps.length}
          </span>
          <span className="text-sm font-medium text-[#40A1AB]">{steps[currentStep - 1]}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-[#40A1AB] h-2 rounded-full transition-all duration-300"
            style={{ width: `${(currentStep / steps.length) * 100}%` }}
          />
        </div>
      </div>
    </div>
  )
}
